<template>
  <div>
    <ReportCard
      v-if="report && type === 'Report'"
      :report="report"
      :hide-buttons="!showReportActions"
      class="mb-5"
    />
    <v-card class="comment-box mt-3" variant="outlined">
      <v-card-title
        class="d-flex align-center px-4 py-3 bg-amber-lighten-5 comment-title"
      >
        <v-icon start color="amber-darken-1" class="mr-2"
          >mdi-comment-multiple</v-icon
        >
        Reacties
        <v-chip
          v-if="comments.length > 0"
          class="ml-2"
          size="small"
          color="amber-lighten-1"
          variant="tonal"
        >
          {{ comments.length }}
        </v-chip>
      </v-card-title>
      <v-card-text class="pa-0">
        <div v-if="loading" class="d-flex justify-center align-center pa-6">
          <v-progress-circular
            indeterminate
            color="amber-lighten-2"
            size="32"
          ></v-progress-circular>
        </div>

        <div v-else-if="error" class="comment-section">
          <v-alert type="error" variant="tonal">
            {{ error }}
            <div class="d-flex justify-center mt-2">
              <v-btn color="error" variant="tonal" @click="retryLoad">
                <v-icon start>mdi-refresh</v-icon>
                Opnieuw proberen
              </v-btn>
            </div>
          </v-alert>
        </div>

        <div v-else>
          <!-- Comments list -->
          <v-list
            v-if="comments.length > 0 && !loadError"
            class="comment-list pa-0"
          >
            <div
              v-for="comment in comments"
              :key="comment.id"
              :class="{ 'new-comment-animation': isNewComment(comment.id) }"
            >
              <CommentItem
                :comment="comment"
                :color="
                  Number(comment.id) === Number(selectedComment?.id)
                    ? 'bg-amber-lighten-5'
                    : ''
                "
                @edit_comment="setComment"
                @comment_deleted="handleCommentDeleted"
              />
            </div>
          </v-list>

          <div v-else-if="!loadError" class="empty-comment-state">
            <v-icon size="large" color="grey-lighten-1" class="mb-2"
              >mdi-comment-outline</v-icon
            >
            <div>Geen reacties. Wees de eerste om te reageren!</div>
          </div>

          <!-- Comment form -->
          <div class="comment-form-section">
            <CommentForm
              :id="id"
              :type="type"
              :comment_edit="selectedComment"
              @comment-reset="resetComment"
              @comment-created="handleCommentCreated"
              @comment-updated="handleCommentUpdated"
            />
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from "vue";
import { useCommentsStoreRefactor } from "@/stores/commentsStoreRefactor";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useRoute } from "vue-router";
import CommentItem from "./CommentItem.vue";
import CommentForm from "./CommentForm.vue";
import ReportCard from "../reports/ReportCard.vue";

export default {
  name: "CommentBox",
  components: {
    CommentItem,
    CommentForm,
    ReportCard,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    id: {
      type: [Number, String],
      required: true,
    },
    report: {
      type: Object,
      default: null,
    },
    showReportActions: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const commentsStore = useCommentsStoreRefactor();
    const usersStore = useUsersStoreRefactor();
    const route = useRoute();

    const selectedComment = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const loadError = ref(false);
    const highlightedCommentId = ref(null);
    const newCommentIds = ref(new Set()); // Track newly added comments for animation

    // Get the highlighted comment ID from query parameters
    const commentIdFromQuery = computed(() => {
      return route.query.comment ? Number(route.query.comment) : null;
    });

    // Directly use a computed property from the store - Vue reactivity will handle updates
    const comments = computed(() =>
      commentsStore.getCommentsByTypeAndId(props.type, props.id),
    );

    // Watch for new comments and animate them
    watch(
      comments,
      (newComments, oldComments) => {
        if (!oldComments || oldComments.length === 0) return;

        // Find newly added comments
        const oldIds = new Set(oldComments.map((c) => c.id));
        const newlyAdded = newComments.filter((c) => !oldIds.has(c.id));

        if (newlyAdded.length > 0) {
          console.log(
            "CommentBox: Detected new comments for animation:",
            newlyAdded.map((c) => c.id),
          );

          // Add new comment IDs to our tracking set
          newlyAdded.forEach((comment) => {
            newCommentIds.value.add(comment.id);
          });

          // Wait for next tick to ensure DOM is updated, then trigger animation
          nextTick(() => {
            console.log(
              "CommentBox: Applied animation classes to new comments",
            );

            // Remove the animation class after a delay
            setTimeout(() => {
              newlyAdded.forEach((comment) => {
                console.log(
                  "CommentBox: Removing animation for comment:",
                  comment.id,
                );
                newCommentIds.value.delete(comment.id);
              });
            }, 3000); // 3 seconds animation
          });
        }
      },
      { deep: true },
    );

    // Function to check if a comment should be animated
    const isNewComment = (commentId) => {
      const shouldAnimate = newCommentIds.value.has(commentId);
      if (shouldAnimate) {
        console.log("CommentBox: Comment", commentId, "should be animated");
      }
      return shouldAnimate;
    };

    // Simple function to load data once
    const loadData = async () => {
      if (!props.type || !props.id) {
        error.value = "Type and ID are required";
        loadError.value = true;
        loading.value = false;
        return;
      }

      console.log(
        `CommentBox: Loading comments for ${props.type} with ID ${props.id}`,
      );

      loading.value = true;
      error.value = null;
      loadError.value = false;

      try {
        // Simply fetch comments from the store - they'll automatically appear in our computed property
        await commentsStore.fetchCommentsByTypeAndId(props.type, props.id);

        // Check if we need to highlight a comment after data loads
        if (commentIdFromQuery.value) {
          highlightComment(commentIdFromQuery.value);
        }
      } catch (err) {
        error.value = err.message || "Error loading comments";
        loadError.value = true;
        console.error("Error loading comments:", err);
      } finally {
        loading.value = false;
      }
    };

    // Simplified highlight function - no need for multiple attempts
    const highlightComment = async (commentId) => {
      console.log(`CommentBox: Highlighting comment with ID ${commentId}`);
      highlightedCommentId.value = commentId;

      // Wait for DOM to update with a single nextTick
      await nextTick();

      // Try to find the comment element
      const commentSelector = `#c${commentId}, #C${commentId}`;
      const commentElement = document.querySelector(commentSelector);

      if (commentElement) {
        console.log("CommentBox: Comment element found, scrolling to it");

        // Scroll the comment into view
        commentElement.scrollIntoView({ behavior: "smooth", block: "center" });

        // Add highlight animation
        commentElement.classList.add("highlight-comment");

        // Remove highlight after animation
        setTimeout(() => {
          commentElement.classList.remove("highlight-comment");
        }, 2000);
      } else {
        console.log(
          `CommentBox: Comment element not found for ID ${commentId}`,
        );
      }
    };

    const retryLoad = () => {
      error.value = null;
      loadError.value = false;
      loadData();
    };

    const setComment = (comment) => {
      if (comment && typeof comment === "object") {
        selectedComment.value = { ...comment };
      }
    };

    const resetComment = () => {
      selectedComment.value = null;
    };

    // Also watch for when handleCommentCreated is called to ensure user's own comments are animated
    const handleCommentCreated = (newComment) => {
      console.log("CommentBox: New comment created:", newComment);

      // Add animation for user's own comment
      if (newComment && newComment.id) {
        console.log(
          "CommentBox: Adding animation for user-created comment:",
          newComment.id,
        );
        newCommentIds.value.add(newComment.id);

        // Remove animation after delay
        setTimeout(() => {
          console.log(
            "CommentBox: Removing animation for user-created comment:",
            newComment.id,
          );
          newCommentIds.value.delete(newComment.id);
        }, 3000);
      }

      // The store already added this comment via the API call
      // The comments computed property will update automatically

      // Just reset the form
      resetComment();
    };

    const handleCommentDeleted = () => {
      // No need to do anything - the store will update and the computed property will react
      console.log("CommentBox: Comment deleted");
    };

    const handleCommentUpdated = () => {
      // No need to do anything - the store will update and the computed property will react
      console.log("CommentBox: Comment updated");
      resetComment();
    };

    // Watch for comment ID in URL
    watch(commentIdFromQuery, (newId) => {
      if (newId && !loading.value) {
        console.log(`CommentBox: Comment ID in URL changed to ${newId}`);
        highlightComment(newId);
      }
    });

    onMounted(async () => {
      console.log("CommentBox: Component mounted");
      try {
        // Load comments and users
        await Promise.all([loadData(), usersStore.getAll()]);
      } catch (err) {
        error.value = "Error initializing comments";
        loadError.value = true;
        console.error("Error in CommentBox setup:", err);
      }
    });

    return {
      comments,
      selectedComment,
      loading,
      error,
      loadError,
      resetComment,
      setComment,
      handleCommentCreated,
      handleCommentDeleted,
      handleCommentUpdated,
      retryLoad,
      highlightedCommentId,
      commentIdFromQuery,
      isNewComment,
    };
  },
};
</script>

<style scoped>
.comment-box {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.comment-list {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.comment-list :deep(.v-list-item-subtitle) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  width: 100%;
}

/* Comment highlight animation */
:deep(.highlight-comment) {
  animation: highlight-pulse 2s ease !important;
  background-color: rgba(
    255,
    193,
    7,
    0.3
  ) !important; /* Using amber color directly */
  border-radius: 8px !important;
  box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.2) !important;
  position: relative !important;
  z-index: 2 !important;
  outline: none !important;
}

/* New comment animation */
.new-comment-animation {
  animation: new-comment-glow 3s ease-out !important;
  border-radius: 8px !important;
}

@keyframes highlight-pulse {
  0% {
    background-color: rgba(255, 193, 7, 0.3) !important;
    transform: scale(1) !important;
  }
  50% {
    background-color: rgba(255, 193, 7, 0.5) !important;
    transform: scale(1.02) !important;
  }
  100% {
    background-color: rgba(255, 193, 7, 0.3) !important;
    transform: scale(1) !important;
  }
}

@keyframes new-comment-glow {
  0% {
    background-color: rgba(76, 175, 80, 0.6) !important; /* Green glow */
    transform: scale(1.02) !important;
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.4) !important;
  }
  50% {
    background-color: rgba(76, 175, 80, 0.3) !important;
    transform: scale(1.01) !important;
    box-shadow: 0 0 10px rgba(76, 175, 80, 0.2) !important;
  }
  100% {
    background-color: transparent !important;
    transform: scale(1) !important;
    box-shadow: none !important;
  }
}

.comment-section {
  padding: 16px;
}

.comment-form-section {
  padding: 16px;
}

.empty-comment-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
}

/* Mobile optimizations */
@media (max-width: 600px) {
  .comment-title {
    padding: 10px 12px;
  }

  .comment-section,
  .comment-form-section {
    padding: 12px;
  }

  .empty-comment-state {
    padding: 16px;
  }

  .comment-box {
    border-radius: 4px;
  }
}
</style>
